import { memo, useEffect, useMemo, useRef, useState } from 'react';
import ITimeLeft from '../../interfaces/ITimeLeft';
import ICountdownTimerProps from './interfaces/ICountdownProps';
import { getTimeLeft } from '../../../utils/date-utils';

const Countdown = memo(function Countdown({
    targetDateTime,
    countdownEndCallback,
}: Readonly<ICountdownTimerProps>) {
    const [timeLeft, setTimeLeft] = useState<ITimeLeft>(
        getTimeLeft(targetDateTime),
    );

    const hasCalledCallbackRef = useRef(false);
    const countdownEndCallbackRef = useRef(countdownEndCallback);

    useEffect(() => {
        countdownEndCallbackRef.current = countdownEndCallback;
    }, [countdownEndCallback]);

    useEffect(() => {
        hasCalledCallbackRef.current = false;

        if (!targetDateTime) {
            return () => {};
        }

        const intervalId = setInterval(() => {
            const newTimeLeft = getTimeLeft(targetDateTime);
            setTimeLeft(newTimeLeft);

            const isCountdownZero =
                newTimeLeft.hours === 0 &&
                newTimeLeft.minutes === 0 &&
                newTimeLeft.seconds === 0;

            if (
                isCountdownZero &&
                !hasCalledCallbackRef.current &&
                countdownEndCallbackRef.current
            ) {
                hasCalledCallbackRef.current = true;
                countdownEndCallbackRef.current();
                clearInterval(intervalId);
            }
        }, 1000);

        return () => {
            clearInterval(intervalId);
        };
    }, [targetDateTime]);

    const formattedTimer = useMemo(() => {
        const { hours, minutes, seconds } = timeLeft;
        const formattedHours = hours > 0 ? `${hours}:` : '';
        const formattedMinutes = minutes >= 10 ? `${minutes}:` : `0${minutes}:`;
        const formattedSeconds = seconds >= 10 ? `${seconds}` : `0${seconds}`;

        return `${formattedHours}${formattedMinutes}${formattedSeconds}`;
    }, [timeLeft]);

    return (
        <div className="countdown" data-testid="countdown">
            {formattedTimer}
        </div>
    );
});

export default Countdown;
